import { Plus } from 'lucide-react'
import { useState } from 'react'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import { Button } from '@/components/ui/button'
import { type ApiTokenListItem,ApiTokens } from '@/containers/ApiTokens/ApiTokens'
import { OAuthAuthorizations } from '@/containers/OAuthAuthorizations/OAuthAuthorizations'
import { OAuthClients } from '@/containers/OAuthClients/OAuthClients'
import { SettingsPage, SettingsSection } from '@/containers/Settings/SettingsLayout'
import { WebhookSecret } from '@/containers/WebhookSecret/WebhookSecret'
import { apiFetcher } from '@/data/fetcher'
import { WebhookForm } from '@/forms/WebhookForm/WebhookForm'
import type { OAuthAuthorization, OAuthClient } from '@/interfaces/OAuth'

export default function SettingsApiPage() {
  const [creating, setCreating] = useState(false)

  const {
    data: settings,
    isLoading,
    mutate,
  } = useSWR<{
    tokens: ApiTokenListItem[]
    webhookUrl: string | null
    /** Klíč pro podpis webhooků, starší backend ho neposílá. */
    webhookSecret?: string
  }>('/api/v2/settings/api', apiFetcher)

  // While the backend has no OAuth endpoints, these fail and the sections stay hidden.
  const oauthClients = useSWR<OAuthClient[]>('/api/v2/settings/oauth-clients', apiFetcher)
  const oauthAuthorizations = useSWR<OAuthAuthorization[]>(
    '/api/v2/settings/oauth-authorizations',
    apiFetcher,
  )
  const [creatingClient, setCreatingClient] = useState(false)

  return (
    <SettingsPage
      title="API a webhooky"
      description="Propojte invo.space s e-shopem, účetnictvím nebo vlastními skripty."
      breadcrumbHref="/settings/api"
    >
      {isLoading && <Loading />}

      {settings && (
        <>
          <SettingsSection
            title="API tokeny"
            description="Každé integraci vytvořte vlastní token. Když ho přestanete potřebovat, smažete jen ten jeden."
            actions={
              <Button type="button" variant="outline" size="sm" onClick={() => setCreating(true)} disabled={creating}>
                <Plus />
                Nový token
              </Button>
            }
          >
            <ApiTokens
              tokens={settings.tokens}
              mutate={() => mutate()}
              creating={creating}
              onCreatingChange={setCreating}
            />
          </SettingsSection>

          {oauthClients.data && (
            <SettingsSection
              title="OAuth aplikace"
              description="Vaše aplikace, které se přihlašují přes OAuth 2.0 a žádají uživatele o přístup."
              actions={
                <Button type="button" variant="outline" size="sm" onClick={() => setCreatingClient(true)} disabled={creatingClient}>
                  <Plus />
                  Nová aplikace
                </Button>
              }
            >
              <OAuthClients
                clients={oauthClients.data}
                mutate={() => oauthClients.mutate()}
                creating={creatingClient}
                onCreatingChange={setCreatingClient}
              />
            </SettingsSection>
          )}

          {oauthAuthorizations.data && (
            <SettingsSection
              title="Povolené aplikace"
              description="Aplikace, kterým jste povolili přístup ke svému účtu."
            >
              <OAuthAuthorizations
                authorizations={oauthAuthorizations.data}
                mutate={() => oauthAuthorizations.mutate()}
              />
            </SettingsSection>
          )}

          <SettingsSection
            title="Webhooky"
            description="Na zadanou adresu pošleme POST požadavek, když nastane některá z událostí níže."
          >
            <div className="space-y-6">
              <WebhookForm defaultValues={{ webhookUrl: settings.webhookUrl ?? '' }} />
              {settings.webhookSecret && <WebhookSecret secret={settings.webhookSecret} onRotated={() => mutate()} />}
            </div>
          </SettingsSection>
        </>
      )}
    </SettingsPage>
  )
}
